/**
 * ActorNetworking - networking types and RPC decorators.
 * Contains all networking-related types, enums, and RPC decorator implementations.
 */

import 'reflect-metadata';
import * as THREE from 'three';

import { property } from '../utils/serialization/decorator.js';

import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';

/**
 * Replicated transform state for simple actors.
 * Used by {@link Actor.netTransform} when {@link Actor.replicateTransform} is `true`.
 * Characters should use {@link PawnNetTransform} instead.
 */
export class ActorNetTransform {
  @property({ replicate: true, net: { netType: 'vector3', quantization: { mode: 'scale', scale: 1000 } } })
  public position: THREE.Vector3 = new THREE.Vector3();

  @property({ replicate: true, net: { netType: 'euler', quantization: { mode: 'range', min: -3.141592653589793, max: 3.141592653589793, bits: 16 } } })
  public rotation: THREE.Euler = new THREE.Euler();

  @property({ replicate: true, net: { netType: 'vector3', quantization: { mode: 'range', min: 0.01, max: 100, bits: 12 } } })
  public scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);
}

/**
 * Replicated transform state for character actors (Pawn).
 * Extends {@link ActorNetTransform} with a server-side timestamp required for
 * movement prediction and interpolation on clients.
 */
export class PawnNetTransform extends ActorNetTransform {
  @property({ replicate: true, net: { netType: 'float', quantization: { mode: 'scale', scale: 1000 } } })
  public timestamp: number = 0;
}

/** Network role enum - describes authority and replication behavior */
export enum NetRole {
  None = 'None',
  Authority = 'Authority',
  AutonomousProxy = 'AutonomousProxy',
  SimulatedProxy = 'SimulatedProxy'
}

/** Options for RPC calls */
export interface RPCOptions {
  /** Whether the RPC should be sent reliably (guaranteed delivery, ordered) */
  reliable?: boolean;
  /**
   * Per-parameter binary metadata overrides for RPC arguments.
   *
   * Each element corresponds to a method parameter by position:
   * - `null` or `undefined` → use the generator-inferred metadata for that parameter.
   * - A partial object → merged with the inferred metadata at **generator time**
   *   (same rules as `@property({ net: { ... } })`).
   * - A full object → used as-is; overrides inferred metadata entirely.
   *
   * When the whole array is omitted the generator-inferred metadata from
   * `engine-data.ts` is used for all parameters.
   */
  params?: (Partial<NetPropertyMetadata> | null | undefined)[];
}

/** RPC method types */
export enum RPCType {
  Server = 'Server',      // Client → Server
  Client = 'Client',      // Server → Client(s)
  Multicast = 'Multicast', // Server → All Clients (+ Server)
  Remote = 'Remote'       // Bidirectional (Client ↔ Server)
}

/** RPC method metadata */
export interface RPCMethodMetadata {
  methodName: string;
  type: RPCType;
  options: RPCOptions;
}

export const RPC_METADATA_KEY = Symbol('rpc:methods');

function registerRPCMetadata(target: any, metadata: RPCMethodMetadata): void {
  const existingMetadata = Reflect.getMetadata(RPC_METADATA_KEY, target.constructor) ?? [];
  existingMetadata.push(metadata);
  Reflect.defineMetadata(RPC_METADATA_KEY, existingMetadata, target.constructor);
}

/** Interface for RPC sender (implemented by NetWorld) */
export interface IRPCSender {
  clientRPC(actor: IRPCTarget, methodName: string, args: any[], options: RPCOptions): void;
  multicastRPC(actor: IRPCTarget, methodName: string, args: any[], options: RPCOptions): void;
  serverRPC(actor: IRPCTarget, methodName: string, args: any[], options: RPCOptions): void;
}

/** Interface for objects that support RPC functionality */
export interface IRPCTarget {
  readonly name: string;
  hasAuthority(): boolean;
  isLocalAutonomousProxy(): boolean;
  isOwnedByLocalClient(): boolean;
  getNetObjectId(): string;
  getNetSubId?(): string | null;
  netOwningClientId: string | null;
  getWorld(): { netWorld: IRPCSender } | null;
}

/**
 * Marks a method as a Server RPC (Client → Server)
 * The method will only be executed on the server when received. Does NOT execute locally.
 * @param options - RPC options
 * @example
 * ```typescript
 * class MyPawn extends Pawn {
 *   @ServerRPC({ reliable: true })
 *   public validateJump() {
 *     // This ONLY runs on server
 *   }
 * }
 * ```
 */
export function ServerRPC(options: Partial<RPCOptions> = {}) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    // Store metadata for validation
    const metadata: RPCMethodMetadata = {
      methodName: propertyKey,
      type: RPCType.Server,
      options: { reliable: true, ...options }
    };

    registerRPCMetadata(target, metadata);

    // Replace the method
    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      // Send to server if we're an autonomous proxy (does NOT execute locally)
      if (this.isLocalAutonomousProxy() && this.isOwnedByLocalClient()) {
        this.getWorld()?.netWorld.serverRPC(this, propertyKey, args, metadata.options);
      } else if (this.hasAuthority()) {
        // Execute on server when called locally or received via network
        return originalMethod.apply(this, args);
      }
    };

    return descriptor;
  };
}

/**
 * Marks a method as a Client RPC (Server → Owning Client)
 * The method will only be executed on the owning client when received. Does NOT execute locally.
 * If actor has no owner, the RPC is not sent (use @MulticastRPC for non-owned actors)
 * @param options - RPC options (reliability)
 * @example
 * ```typescript
 * class MyPawn extends Pawn {
 *   @ClientRPC()
 *   public showDamageIndicator() {
 *     // This ONLY runs on the owning client
 *   }
 * }
 * ```
 */
export function ClientRPC(options: Partial<RPCOptions> = {}) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    // Store metadata
    const metadata: RPCMethodMetadata = {
      methodName: propertyKey,
      type: RPCType.Client,
      options: { reliable: true, ...options }
    };

    registerRPCMetadata(target, metadata);

    // Replace the method
    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      // Send to clients if we have authority (does NOT execute locally on server)
      if (this.hasAuthority()) {
        this.getWorld()?.netWorld.clientRPC(this, propertyKey, args, metadata.options);
      } else {
        // Execute on client when received via network
        return originalMethod.apply(this, args);
      }
    };

    return descriptor;
  };
}

/**
 * Marks a method as a Multicast RPC (Server → All Clients + Server)
 * - On Server: Executes locally AND sends to all clients
 * - On Client: Does nothing when called (clients only execute when received)
 * Useful for events that all players should see (explosions, announcements, etc.)
 * @param options - RPC options (reliability)
 * @example
 * ```typescript
 * class Grenade extends Actor {
 *   @MulticastRPC()
 *   public explode() {
 *     // Runs on server AND all clients
 *   }
 * }
 * ```
 */
export function MulticastRPC(options: Partial<RPCOptions> = {}) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    // Store metadata
    const metadata: RPCMethodMetadata = {
      methodName: propertyKey,
      type: RPCType.Multicast,
      options: { reliable: true, ...options }
    };

    registerRPCMetadata(target, metadata);

    // Replace the method
    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      let result: any;

      if (this.hasAuthority()) {
        // Execute locally on server AND send to all clients
        result = originalMethod.apply(this, args);
        this.getWorld()?.netWorld.multicastRPC(this, propertyKey, args, metadata.options);
      } else {
        // Execute on client when received via network
        result = originalMethod.apply(this, args);
      }

      return result;
    };

    return descriptor;
  };
}

/**
 * Marks a method as a Remote RPC (Bidirectional)
 * - On Client (AutonomousProxy): Sends to server, executes locally on server when received
 * - On Server (Authority): Sends to clients, executes locally on clients when received
 * The method only executes on the receiving machine.
 * @param options - RPC options
 */
export function RemoteRPC(options: Partial<RPCOptions> = {}) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    // Store metadata
    const metadata: RPCMethodMetadata = {
      methodName: propertyKey,
      type: RPCType.Remote,
      options: { reliable: true, ...options }
    };

    registerRPCMetadata(target, metadata);

    // Replace the method
    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      // Determine direction based on authority (does NOT execute locally)
      if (this.hasAuthority()) {
        // Server → Clients (does not execute on server)
        this.getWorld()?.netWorld.clientRPC(this, propertyKey, args, metadata.options);
      } else if (this.isLocalAutonomousProxy() && this.isOwnedByLocalClient()) {
        // Client → Server (does not execute on client)
        this.getWorld()?.netWorld.serverRPC(this, propertyKey, args, metadata.options);
      } else {
        // Execute when received from remote
        return originalMethod.apply(this, args);
      }
    };

    return descriptor;
  };
}

/**
 * Marks a method as Server-Only (only executes on server, not sent anywhere)
 * Useful for server-side validation and logic that doesn't need to be replicated.
 */
export function ServerOnly() {
  return function (
    _target: any,
    _propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      // Only execute on server
      if (this.hasAuthority()) {
        return originalMethod.apply(this, args);
      }
    };

    return descriptor;
  };
}

/**
 * Marks a method as Client-Only (only executes on clients, not sent anywhere)
 * Useful for cosmetic effects that don't need to run on server.
 */
export function ClientOnly() {
  return function (
    _target: any,
    _propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = function (this: IRPCTarget, ...args: any[]) {
      // Only execute on clients
      if (!this.hasAuthority()) {
        return originalMethod.apply(this, args);
      }
    };

    return descriptor;
  };
}